// EXIF read via exifr (MIT): orientation, capture date, artist, copyright, GPS presence.
import * as exifr from 'exifr';
import type { ExifSummary } from './decode/types';
import { isWebp, readWebp } from './decode/webp';

/**
 * exifr 7.1.3 has no WebP parser. A WebP's EXIF is a TIFF block in its "EXIF" chunk, which comes AFTER
 * the image data, so the whole file is read; that block goes to exifr like any TIFF.
 */
async function webpExif(data: Blob | ArrayBuffer | Uint8Array): Promise<Uint8Array | null | undefined> {
  const head =
    data instanceof Blob
      ? new Uint8Array(await data.slice(0, 12).arrayBuffer())
      : data instanceof Uint8Array
        ? data.subarray(0, 12)
        : new Uint8Array(data, 0, Math.min(12, data.byteLength));
  if (!isWebp(head)) return undefined; // not a WebP: exifr reads it as before
  const all =
    data instanceof Blob
      ? new Uint8Array(await data.arrayBuffer())
      : data instanceof Uint8Array
        ? data
        : new Uint8Array(data);
  return readWebp(all)?.exif ?? null;
}

export async function readExif(data: Blob | ArrayBuffer | Uint8Array): Promise<ExifSummary> {
  try {
    const w = await webpExif(data);
    if (w === null) return { orientation: 1, hasGps: false }; // a WebP without EXIF
    if (w) data = w;
    const t = (await exifr.parse(data, {
      pick: [
        'Orientation',
        'DateTimeOriginal',
        'CreateDate',
        'Artist',
        'Copyright',
        'GPSLatitude',
        'GPSLongitude',
      ],
      translateValues: false,
      reviveValues: true,
      gps: true,
    })) as Record<string, unknown> | undefined;
    if (!t) return { orientation: 1, hasGps: false };
    const d = (t.DateTimeOriginal ?? t.CreateDate) as Date | string | undefined;
    const date = d instanceof Date && !Number.isNaN(d.getTime()) ? d.toISOString().slice(0, 10) : undefined;
    const orientation =
      typeof t.Orientation === 'number' && t.Orientation >= 1 && t.Orientation <= 8 ? t.Orientation : 1;
    return {
      orientation,
      date,
      artist: typeof t.Artist === 'string' ? t.Artist : undefined,
      copyright: typeof t.Copyright === 'string' ? t.Copyright : undefined,
      hasGps: t.GPSLatitude !== undefined || t.GPSLongitude !== undefined || t.latitude !== undefined,
    };
  } catch {
    return { orientation: 1, hasGps: false };
  }
}
